import React, { useEffect, useRef, useState } from 'react'
import {
  Container,
  Row,
  Horizontal,
  Column,
  Text,
  Spacer
} from '@smuchile/pkg-unimarc-components'
import { TotalizerButtons } from './TotalizerButtons'
import {
  BigScreen,
  getBemId,
  getGlobalStyle,
  SmallScreen
} from '@smuchile/pkg-unimarc-components/helpers'
import {
  Cookies,
  eventClick,
  formatSubtotal,
  getCurrentDomain,
  isValidArrayWithData,
  ItemFields,
  trigger,
  useEvents,
  useMobile,
  useOrderForm
} from '@smuchile/pkg-unimarc-hooks'
import { ICart } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/INewCartUnimarc'
import { TooltipWrapper } from '../TooltipWrapper'
import { SubMenu } from 'components/SubMenu/SubMenu'
import { ERROR_MESSAGES } from 'components/Body/helper/constants'
import { TotalUnipay } from './TotalUnipay'
import { MembershipToast } from '../TooltipWrapper/MembershipToast'
import { ISimulationResponse } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/ISimulation'
import { COOKIES_KEYS } from 'shared/utils/constants'

export interface TotalizersProps {
  cartData: ICart
  createListButton: boolean
  isFetchingCart: boolean
  isFetchingSimulation?: boolean
  isLoadingByEvent: boolean
  isLoadingCart: boolean
  isLoadingMyUsualsAndFavorites: boolean
  limitOnProductAlert: boolean
  notificationAddToList: boolean
  openMyUsuals: boolean
  selectedListName: string
  simulationData?: ISimulationResponse
  toastMembership?: {
    item: ItemFields
    savings: string
    showIndexingMembership: boolean
    showSavings: boolean
    showSavingsToast: boolean
    showToast: boolean
    handleCloseToast: () => void
    handleSavingsToast: () => void
  }
  handleOpenMyUsualsAndFavorites: () => void
  handleOnClosed: () => void
  handleOpenMyListsModal: () => void
  refetchSavings: () => void
  refetchSimulation: () => void
  setLimitOnProductAlert: (value: boolean) => void
  setNotificationAddToList: (type: boolean) => void
}

export const Totalizers = ({
  cartData,
  createListButton,
  isFetchingCart,
  isFetchingSimulation,
  isLoadingCart,
  isLoadingMyUsualsAndFavorites,
  isLoadingByEvent,
  limitOnProductAlert,
  notificationAddToList,
  openMyUsuals,
  selectedListName,
  simulationData,
  toastMembership,
  handleOpenMyUsualsAndFavorites,
  handleOnClosed,
  handleOpenMyListsModal,
  refetchSavings,
  refetchSimulation,
  setLimitOnProductAlert,
  setNotificationAddToList
}: TotalizersProps): React.ReactElement => {
  const domain = getCurrentDomain()
  const { data: queryOrderForm, isLoading: isLoadingQueryOrderForm } =
    useOrderForm()

  const [showTooltipError, setShowTooltipError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [codeError, setCodeError] = useState('')
  const [alcoholLaw, setAlcoholLaw] = useState(false)
  const [toastMessage, setToastMessage] = useState({
    show: false,
    message: ''
  })
  const [showMyUsuals, setShowMyUsuals] = useState(false)
  const [minValueToBuy, setMinValueToBuy] = useState<string | number>()
  const [tooltip, setTooltip] = useState<boolean>(false)

  const minimumPurchase = queryOrderForm?.data?.summary?.minValue
  const customHeightContainer = showMyUsuals ? '77px' : '117px'
  const contentTooltip = useRef<HTMLDivElement>()
  const { innerWidth } = useMobile()
  const isMobile = innerWidth < 1280
  const _isLoading = Boolean(
    !cartData || isLoadingCart || isLoadingByEvent || !cartData?.subtotal
  )
  const disableSubTotals = _isLoading
    ? getGlobalStyle('--color-primary-disabled')
    : getGlobalStyle('--color-base-black')

  const showTooltip =
    alcoholLaw ||
    toastMessage.show ||
    notificationAddToList ||
    limitOnProductAlert ||
    showTooltipError ||
    toastMembership?.showToast

  const showMembershipToast =
    toastMembership?.showIndexingMembership ||
    (toastMembership?.showSavingsToast && toastMembership?.showSavings)

  useEvents({
    eventType: 'myUsuals',
    callBack: ({ detail: { show } }) => {
      setShowMyUsuals(show)
    }
  })
  useEvents({
    eventType: 'productNoteNotification',
    callBack: ({ detail: { show, txt } }) => {
      setToastMessage({
        show,
        message: txt
      })
      addAnimationAndCloseTooltip()
    }
  })
  useEvents({
    eventType: 'updateItemQuantity',
    callBack: ({ detail: { error, errorCode } }) => {
      if (error === true) {
        setShowTooltipError(error)
        setErrorMessage(ERROR_MESSAGES.errorChangingQuantity)
        setCodeError(errorCode)
      }
    }
  })

  useEvents({
    eventType: 'clearCart',
    callBack: ({ detail: { error } }) => {
      if (error === true) {
        setShowTooltipError(error)
        setErrorMessage(ERROR_MESSAGES.errorCleaningCart)
      }
    }
  })

  useEvents({
    eventType: 'updateQuantityDone',
    callBack: () => {
      refetchSavings()
      refetchSimulation()
    }
  })

  useEvents({
    eventType: 'toastMessage',
    callBack: ({ detail: { message, isError } }) => {
      if (isError) {
        setShowTooltipError(isError)
        setErrorMessage(message)
      } else {
        setToastMessage({ show: true, message })
      }
    }
  })

  useEvents({
    eventType: 'updateItemQuantity',
    callBack: ({ detail: { error, errorCode } }) => {
      if (error === true) {
        setShowTooltipError(error)
        setErrorMessage(ERROR_MESSAGES.errorChangingQuantity)
        setCodeError(errorCode)
      }
    }
  })

  const setClassAnimation = () => {
    contentTooltip.current?.classList?.add('animateTooltip')
  }

  const addAnimationAndCloseTooltip = () => {
    setTimeout(() => {
      setClassAnimation()
    }, 3000)
    Cookies.remove('showAddressChangedTooltip', {
      domain,
      path: '/'
    })
    setTimeout(() => {
      setAlcoholLaw(false)
      setLimitOnProductAlert(false)
      setNotificationAddToList(false)
      setShowTooltipError(false)
      setToastMessage({ show: false, message: '' })
    }, 4000)
  }

  const closeAlcoholLaw = () => {
    setClassAnimation()
    Cookies.remove('showAddressChangedTooltip', {
      domain,
      path: '/'
    })
    setTimeout(() => {
      setAlcoholLaw(false)
      setToastMessage({ show: false, message: '' })
      toastMembership?.handleCloseToast()
    }, 1000)
  }

  const closeLimitOnProductAlert = () => {
    setClassAnimation()
    setTimeout(() => {
      setLimitOnProductAlert(false)
    }, 1000)
  }

  const closeTooltipError = () => {
    setClassAnimation()
    setTimeout(() => {
      setShowTooltipError(false)
    }, 1000)
  }

  const handleOpen = () => {
    trigger({ eventType: 'clearCart', data: { show: true } })
    eventClick({
      event: 'interacciones_carrito',
      eventAction: 'navegacion_compra',
      eventCategory: 'carrito',
      eventLabel: 'vaciar carro'
    })
  }

  useEffect(() => {
    if (cartData?.needIdentification) {
      setAlcoholLaw(true)
      addAnimationAndCloseTooltip()
    }
  }, [cartData?.needIdentification])

  useEffect(() => {
    if (!isLoadingQueryOrderForm && !isFetchingCart && minimumPurchase) {
      const newSubtotal = formatSubtotal(cartData?.subtotal).toString()
      const minimumPurchaseFormated = formatSubtotal(minimumPurchase).toString()
      setMinValueToBuy(minimumPurchase)
      parseInt(newSubtotal) <= parseInt(minimumPurchaseFormated)
        ? setTooltip(true)
        : setTooltip(false)
    }
  }, [
    cartData,
    isFetchingCart,
    isLoadingByEvent,
    isLoadingQueryOrderForm,
    minimumPurchase,
    queryOrderForm?.data?.summary?.minValue
  ])

  useEffect(() => {
    if (!isLoadingCart && !isLoadingQueryOrderForm) {
      const showTooltip = JSON.parse(
        Cookies.get('showAddressChangedTooltip') || 'false'
      )
      setToastMessage({
        show: showTooltip,
        message: 'Tu dirección fue agregada con éxito 🥳'
      })
    }
  }, [isLoadingCart, isLoadingQueryOrderForm])

  useEffect(() => {
    if (toastMembership?.showToast) {
      setToastMessage({
        show: true,
        message: '¡Listo! Ya estás disfrutando tus descuentos Socio Diamante.'
      })
    }
  }, [toastMembership?.showToast])

  useEffect(() => {
    if (isLoadingCart) return
    const cookieToastValue = JSON.parse(
      Cookies.get(COOKIES_KEYS.SHOW_TOAST) || '{}'
    )
    if (cookieToastValue?.show) {
      setToastMessage({
        show: cookieToastValue?.show,
        message: cookieToastValue?.toastMessage
      })
    }
    Cookies.remove(COOKIES_KEYS.SHOW_TOAST)
  }, [isLoadingCart])

  return (
    <>
      <BigScreen>
        <Container
          id={getBemId('totalizers', 'container')}
          isWrap
          margin={showTooltip ? '-10px 0 0 0' : '0'}
          position='sticky'
          top='32'
        >
          <Column justifyContent='start'>
            {showMembershipToast && (
              <Column
                justifyContent='end'
                maxWidth='max-content'
                minHeight='70px'
              >
                <MembershipToast
                  handleSavingsToast={toastMembership?.handleSavingsToast}
                  isLoadingByEvent={isLoadingByEvent}
                  item={toastMembership?.item}
                  savings={toastMembership?.savings}
                  showIndexingMembership={
                    toastMembership?.showIndexingMembership
                  }
                />
                {!showTooltip && <Spacer.Horizontal size={12} />}
              </Column>
            )}
            {showTooltip && !openMyUsuals && (
              <Column
                justifyContent='end'
                maxWidth='max-content'
                minHeight='70px'
                ref={contentTooltip}
              >
                <TooltipWrapper
                  alcoholLaw={alcoholLaw}
                  closeAlcoholLaw={closeAlcoholLaw}
                  closeLimitOnProductAlert={closeLimitOnProductAlert}
                  closeTooltipError={closeTooltipError}
                  codeError={codeError}
                  createListButton={createListButton}
                  errorMessage={errorMessage}
                  handleOnClosed={handleOnClosed}
                  isMobile={isMobile}
                  limitOnProductAlert={limitOnProductAlert}
                  notificationAddToList={notificationAddToList}
                  selectedListName={selectedListName}
                  setLimitOnProductAlert={setLimitOnProductAlert}
                  setNotificationAddToList={setNotificationAddToList}
                  showTooltipError={showTooltipError}
                  toastMessage={toastMessage}
                />
              </Column>
            )}
          </Column>
          {showTooltip && <Spacer.Horizontal size={12} />}
          <Row>
            <Column
              maxHeight='188px'
              maxWidth='377px'
              minHeight='188px'
              minWidth='377px'
              position='sticky'
              top='110'
            >
              <Row
                backgroundColor={getGlobalStyle('--color-base-white')}
                border={`0.3px solid ${getGlobalStyle(
                  '--color-neutral-gray-divider'
                )}`}
                borderRadius={getGlobalStyle('--border-radius-md')}
                isWrap
                padding='24px'
                tagName='aside'
              >
                <Row
                  id={getBemId('subtotal', 'container')}
                  justifyContent='between'
                >
                  <Text
                    customColor={disableSubTotals}
                    fontWeight='semibold'
                    id={getBemId('subtotal', 'text')}
                  >
                    Subtotal
                  </Text>
                  <Text
                    customColor={disableSubTotals}
                    fontWeight='semibold'
                    id={getBemId('subtotalPrice', 'text')}
                  >
                    {cartData?.subtotal}
                  </Text>
                </Row>
                {simulationData &&
                  isValidArrayWithData(simulationData?.simulations) &&
                  !isLoadingQueryOrderForm &&
                  !_isLoading &&
                  !isFetchingSimulation && (
                    <TotalUnipay simulationData={simulationData} />
                  )}
                {tooltip && (
                  <>
                    <Spacer.Horizontal size={18} />
                    <Row
                      alignItems='center'
                      backgroundColor='#FFE5E5'
                      borderRadius='8px'
                      id={getBemId('minimumPrice', 'container')}
                      maxWidth='100%'
                      padding='8px 10px'
                      position='relative'
                    >
                      <Text
                        color='red'
                        fontSize='md'
                        fontWeight='medium'
                        id={getBemId('minimumPrice', 'text')}
                      >
                        {`🛒  Monto mínimo de compra `}
                      </Text>
                      <Spacer.Vertical size={4} />
                      <Text
                        color='red'
                        fontSize='md'
                        fontWeight='semibold'
                        id={getBemId('minimumPrice', 'price')}
                      >
                        ${minValueToBuy}
                      </Text>
                    </Row>
                    <Spacer.Horizontal size={18} />
                  </>
                )}

                {!tooltip && <Spacer.Horizontal size={18} />}
                <TotalizerButtons
                  cartData={cartData}
                  handleOpenMyUsualsAndFavorites={
                    handleOpenMyUsualsAndFavorites
                  }
                  isFetchingSimulation={isFetchingSimulation}
                  isLoading={_isLoading}
                  isLoadingByEvent={isLoadingByEvent}
                  isLoadingMyUsualsAndFavorites={isLoadingMyUsualsAndFavorites}
                  isLoadingQueryOrderForm={isLoadingQueryOrderForm}
                  simulationData={simulationData}
                />
              </Row>
              <Column>
                <Spacer.Horizontal customSize={21} />
                <SubMenu
                  clearCart={handleOpen}
                  disabledButtons={_isLoading}
                  saveList={handleOpenMyListsModal}
                />
              </Column>
            </Column>
          </Row>
        </Container>
      </BigScreen>

      <SmallScreen>
        <>
          {showTooltip && !openMyUsuals && (
            <Column
              margin='5px 0 0 0'
              position='fixed'
              ref={contentTooltip}
              zIndex={getGlobalStyle('--z-index-99999')}
            >
              <TooltipWrapper
                alcoholLaw={alcoholLaw}
                closeAlcoholLaw={closeAlcoholLaw}
                closeLimitOnProductAlert={closeLimitOnProductAlert}
                closeTooltipError={closeTooltipError}
                codeError={codeError}
                createListButton={createListButton}
                errorMessage={errorMessage}
                handleOnClosed={handleOnClosed}
                isMobile={isMobile}
                limitOnProductAlert={limitOnProductAlert}
                notificationAddToList={notificationAddToList}
                selectedListName={selectedListName}
                setLimitOnProductAlert={setLimitOnProductAlert}
                setNotificationAddToList={setNotificationAddToList}
                showTooltipError={showTooltipError}
                toastMessage={toastMessage}
              />
            </Column>
          )}
          {!showMyUsuals && (
            <Container
              absoluteDefault='bottomLeft'
              backgroundColor={getGlobalStyle('--color-base-white')}
              id={getBemId('totalizers', 'container')}
              isWrap
              padding='24px'
              position='fixed'
              zIndex={getGlobalStyle('--z-index-10')}
            >
              <Container
                absoluteDefault='bottomLeft'
                backgroundColor={getGlobalStyle('--color-base-white')}
                borderRadius='16px 16px 0 0'
                boxShadow={getGlobalStyle('--box-shadow-xs')}
                isWrap
                minHeight={customHeightContainer}
                padding='24px'
                position='fixed'
              >
                {showMembershipToast && (
                  <MembershipToast
                    handleSavingsToast={toastMembership?.handleSavingsToast}
                    isLoadingByEvent={isLoadingByEvent}
                    item={toastMembership?.item}
                    savings={toastMembership?.savings}
                    showIndexingMembership={
                      toastMembership?.showIndexingMembership
                    }
                    showingUnipay={
                      isValidArrayWithData(simulationData?.simulations) &&
                      !isLoadingByEvent
                    }
                  />
                )}
                <Container
                  absoluteDefault='bottomLeft'
                  backgroundColor={getGlobalStyle('--color-base-white')}
                  borderRadius='12px 12px 0 0'
                  boxShadow='4px -6px 18px 0px rgba(0, 0, 0, 0.05)'
                  isWrap
                  minHeight={customHeightContainer}
                  padding='20px 16px'
                  position='fixed'
                >
                  <TotalizerButtons
                    cartData={cartData}
                    handleOpenMyUsualsAndFavorites={
                      handleOpenMyUsualsAndFavorites
                    }
                    isLoading={_isLoading}
                    isLoadingByEvent={isLoadingByEvent}
                    isLoadingMyUsualsAndFavorites={
                      isLoadingMyUsualsAndFavorites
                    }
                    isLoadingQueryOrderForm={isLoadingQueryOrderForm}
                    minValueToBuy={minValueToBuy}
                    showMyUsuals={showMyUsuals}
                    simulationData={simulationData}
                    subtotal={cartData?.subtotal}
                    tooltip={tooltip}
                  />
                </Container>
              </Container>
              <Horizontal size={16} />
            </Container>
          )}
        </>
      </SmallScreen>
    </>
  )
}
